<template>
    <div class="datatable">
        <!-- Search Bar -->
        <div class="mb-3 d-flex justify-content-end">
            <div class="input-group" style="max-width: 300px;">
                <input type="text" class="form-control" placeholder="Search..." v-model="searchQuery"
                    @keyup.enter="handleSearch">
                <button class="btn btn-primary" type="button" @click="handleSearch">
                    <i class="fa fa-search"></i>
                </button>
            </div>
        </div>

        <!-- Table -->
        <div class="table-responsive">
            <table class="table table-sm table-hover theme-scrollbar" style="margin-top: 2px !important;">
                <thead class="table-dark">
                    <tr>
                        <!-- {{ Conditionally render select-all checkbox based on selectable and multiSelectable }} -->
                        <th v-if="selectable" class="align-middle text-center" style="width: 40px;">
                            <input v-if="multiSelectable" type="checkbox" class="form-check-input"
                                :checked="isAllSelected" @change="toggleSelectAll">
                        </th>
                        <th class="align-middle" style="width: 50px;">No</th>
                        <th v-for="header in headers" :key="header.value" class="align-middle header-wrap">
                            {{ header.text }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loading">
                        <!-- {{ Adjust colspan based on whether the select column is visible }} -->
                        <td :colspan="headers.length + (selectable ? 2 : 1)" class="text-center p-3">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </td>
                    </tr>
                    <tr v-else-if="!safeItems.length">
                        <!-- {{ Adjust colspan based on whether the select column is visible }} -->
                        <td :colspan="headers.length + (selectable ? 2 : 1)" class="text-center p-3">
                            <i class="fa fa-info-circle me-2"></i>No data available
                        </td>
                    </tr>
                    <tr v-else v-for="(item, index) in safeItems" :key="item.id"
                        :class="{ 'table-active': selectable && selectedItems.includes(item.id) }"
                        @click="selectable ? toggleSelect(item.id) : null"
                        :style="{ cursor: selectable ? 'pointer' : 'default' }">
                        <!-- {{ Conditionally render row checkbox based on selectable }} -->
                        <td v-if="selectable" class="text-center">
                            <input :type="multiSelectable ? 'checkbox' : 'radio'" class="form-check-input"
                                :checked="selectedItems.includes(item.id)"
                                :name="multiSelectable ? undefined : 'singleSelectGroup'" @click.stop
                                @change="toggleSelect(item.id)">
                        </td>
                        <td class="text-center">{{ startIndex + index }}</td>
                        <td v-for="header in headers" :key="header.value" class="align-middle header-wrap">
                            <slot :name="`item.${header.value}`" :item="item">
                                {{ item[header.value] }}
                            </slot>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Footer -->
        <div class="d-flex justify-content-between align-items-center mt-2">
            <div class="d-flex align-items-center">
                <span class="me-2">size:</span>
                <select class="form-select" v-model="localParams.size" @change="handleSizeChange">
                    <option v-for="size in [5, 10, 50, 100, 500]" :key="size" :value="size">
                        {{ size }}
                    </option>
                </select>
            </div>
            <div class="d-flex align-items-center">
                <span class="me-3">
                    {{ startIndex }}-{{ endIndex }} of {{ total }}
                </span>
                <nav>
                    <ul class="pagination mb-0">
                        <li class="page-item" :class="{ disabled: localParams.page === 1 }">
                            <button class="page-link" @click="previousPage">
                                Previous
                            </button>
                        </li>
                        <li class="page-item" :class="{ disabled: endIndex >= total }">
                            <button class="page-link" @click="nextPage">
                                Next
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
// @ts-ignore
import debounce from 'lodash/debounce'

onMounted(() => {
    handleSearch()
})
const props = defineProps<{
    headers: { text: string, value: string, [key: string]: any }[],
    items: { ID: any, [key: string]: any }[] | null, // allow null
    loading?: boolean,
    total: number,
    params: { page: number, size: number, search?: string, [key: string]: any },
    selectable?: boolean,
    multiSelectable?: boolean,
}>()

// Add this computed property to safely handle null items
const safeItems = computed(() => props.items ?? [])

const emit = defineEmits(['update:params', 'selection-change'])

const searchQuery = ref(props.params.search || '') // Initialize with prop value
const localParams = ref({ ...props.params })

const startIndex = computed(() => {
    return (localParams.value.page - 1) * localParams.value.size + 1
})

const endIndex = computed(() => {
    return Math.min(startIndex.value + localParams.value.size - 1, props.total)
})

const handleSearch = debounce(() => {
    localParams.value = {
        ...localParams.value,
        page: 1,
        search: searchQuery.value
    }
    updateParams()
}, 300)

const previousPage = () => {
    if (localParams.value.page > 1) {
        localParams.value.page--
        updateParams()
    }
}

const nextPage = () => {
    if (endIndex.value < props.total) {
        localParams.value.page++
        updateParams()
    }
}

const handleSizeChange = () => {
    localParams.value.page = 1 // Reset to first page when size changes
    updateParams()
}

const updateParams = () => {
    emit('update:params', { ...localParams.value })
}


watch(() => props.params, (newParams) => {
    // Only update if there's an actual difference to prevent infinite loops
    if (JSON.stringify(localParams.value) !== JSON.stringify(newParams)) {
        localParams.value = { ...newParams }
        // Update search query only if it differs to avoid resetting user input during debounce
        if (searchQuery.value !== (newParams.search || '')) {
            searchQuery.value = newParams.search || ''
        }
    }
}, { deep: true })

const selectedItems = ref<Array<any>>([])

const isAllSelected = computed(() => {
    // All selected only makes sense for multi-select
    // @ts-ignore
    return props.selectable && props.multiSelectable && props.items.length > 0 && selectedItems.value.length === props.items.length
})

// {{ Modify toggleSelect based on multiSelectable }}
const toggleSelect = (id: any) => {
    if (!props.selectable) return; // Do nothing if not selectable

    if (props.multiSelectable) {
        // Multi-select logic (existing)
        const index = selectedItems.value.indexOf(id);
        if (index === -1) {
            selectedItems.value.push(id);
        } else {
            selectedItems.value.splice(index, 1);
        }
    } else {
        // Single-select logic
        if (selectedItems.value.includes(id)) {
            // If clicking the already selected item, deselect it
            selectedItems.value = [];
        } else {
            // Select the new item (replacing any previous selection)
            selectedItems.value = [id];
        }
    }
    emit('selection-change', selectedItems.value);
}


// {{ Modify toggleSelectAll to only work if multiSelectable }}
const toggleSelectAll = () => {
    // Only allow select all if multi-select is enabled
    if (!props.selectable || !props.multiSelectable) return;

    if (isAllSelected.value) {
        selectedItems.value = [];
    } else {
        // @ts-ignore
        selectedItems.value = props.items.map(item => item.id);
    }
    emit('selection-change', selectedItems.value);
}


watch(() => props.items, () => {
    // Filter out selected items that are no longer present in the current items list
    selectedItems.value = selectedItems.value.filter(id =>
        // @ts-ignore
        props.items.some(item => item.id === id)
    )
    // If not multi-selectable, ensure only max one item remains selected after items change
    if (props.selectable && !props.multiSelectable && selectedItems.value.length > 1) {
        selectedItems.value = [selectedItems.value[0]]; // Keep only the first one found
        emit('selection-change', selectedItems.value); // Emit change if corrected
    }
}, { deep: true })

// {{ Ensure selection is cleared or reduced to single if multiSelectable changes }}
watch(() => props.multiSelectable, (isMulti) => {
    if (props.selectable) {
        if (!isMulti && selectedItems.value.length > 1) {
            // If switching to single select and multiple are selected, keep only the first
            selectedItems.value = [selectedItems.value[0]];
            emit('selection-change', selectedItems.value);
        }
        // No action needed if switching to multi-select or if selection is already valid
    }
});

// {{ Ensure selection is cleared if selectable changes to false }}
watch(() => props.selectable, (isSelectable) => {
    if (!isSelectable && selectedItems.value.length > 0) {
        selectedItems.value = [];
        emit('selection-change', selectedItems.value);
    }
});

</script>

<style scoped>
.datatable {
    width: 100%;
    overflow-x: auto;
    /* Add horizontal scrolling */
}

.table-active {
    --bs-table-accent-bg: var(--bs-table-active-bg);
    color: var(--bs-table-active-color);
}

.header-wrap {
    white-space: nowrap;
    /* Allow text wrapping */
    word-wrap: break-word;
    /* Break words if necessary */
}

/* {{ Style radio buttons like checkboxes for consistency if desired }} */
/* input[type="radio"].form-check-input {
    border-radius: 0.25em; // Make radios square-ish
} */
</style>